import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Alert,
  Stack,
  TextField,
  alpha,
  Tooltip,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiKeyService } from '../services/apiKeyService';
import { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse } from '../types/api';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { format } from 'date-fns';

export const ApiKeysPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null);
  const [createdKeyDialog, setCreatedKeyDialog] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const { data: apiKeys, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: apiKeyService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: (request: CreateApiKeyRequest) => apiKeyService.create(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setAddDialogOpen(false);
      setNewKeyName('');
      setNewKeyDescription('');
      setCreatedKey(data);
      setCreatedKeyDialog(true);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiKeyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setDeleteConfirmOpen(false);
      setKeyToDelete(null);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiKeyService.toggleStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const handleCreate = () => {
    if (!newKeyName.trim()) return;

    createMutation.mutate({
      name: newKeyName,
      description: newKeyDescription || undefined,
    });
  };

  const handleDelete = (key: ApiKey) => {
    setKeyToDelete(key);
    setDeleteConfirmOpen(true);
  };

  const handleToggle = (key: ApiKey) => {
    toggleMutation.mutate({ id: key.id, isActive: !key.isActive });
  };

  const handleCopyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey.apiKey);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px 0 rgba(6, 182, 212, 0.4)',
              }}
            >
              <VpnKeyIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}
              >
                API Keys
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Manage API keys for agent authentication
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
              boxShadow: '0 4px 14px 0 rgba(6, 182, 212, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
              },
            }}
          >
            Create API Key
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Card
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          {isLoading ? (
            <Box p={6} textAlign="center">
              <Typography color="text.secondary">Loading...</Typography>
            </Box>
          ) : !apiKeys || apiKeys.length === 0 ? (
            <Box p={6} textAlign="center">
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <VpnKeyIcon sx={{ fontSize: 40, color: '#94a3b8' }} />
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No API Keys
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create an API key to authenticate your agents
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                }}
              >
                Create Your First API Key
              </Button>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Last Used</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow
                    key={key.id}
                    sx={{
                      '&:hover': { backgroundColor: '#f8fafc' },
                      '& td': { borderColor: 'divider' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body1" fontWeight={600} color="text.primary">
                        {key.name}
                      </Typography>
                      {key.createdBy && (
                        <Typography variant="caption" color="text.secondary">
                          by {key.createdBy}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {key.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(key.createdAt), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {key.lastUsedAt ? format(new Date(key.lastUsedAt), 'MMM dd, HH:mm') : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {key.isActive ? (
                        <Chip
                          label="Active"
                          size="small"
                          sx={{
                            fontWeight: 500,
                            background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                            color: 'white',
                          }}
                        />
                      ) : (
                        <Chip
                          label="Disabled"
                          size="small"
                          sx={{
                            fontWeight: 500,
                            backgroundColor: '#e2e8f0',
                            color: '#64748b',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={key.isActive ? 'Disable' : 'Enable'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggle(key)}
                            sx={{
                              color: key.isActive ? '#f59e0b' : '#10b981',
                              '&:hover': {
                                backgroundColor: key.isActive
                                  ? alpha('#f59e0b', 0.1)
                                  : alpha('#10b981', 0.1),
                              },
                            }}
                          >
                            {key.isActive ? (
                              <ToggleOffIcon fontSize="small" />
                            ) : (
                              <ToggleOnIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(key)}
                            sx={{
                              color: '#ef4444',
                              '&:hover': { backgroundColor: alpha('#ef4444', 0.1) },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 500 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Create API Key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              fullWidth
              required
              helperText="A descriptive name for this API key"
            />
            <TextField
              label="Description"
              value={newKeyDescription}
              onChange={(e) => setNewKeyDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText="Optional description"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setAddDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newKeyName.trim()}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
              },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created Key Display Dialog */}
      <Dialog
        open={createdKeyDialog}
        onClose={() => {
          setCreatedKeyDialog(false);
          setCreatedKey(null);
          setCopiedToClipboard(false);
        }}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 600 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>API Key Created Successfully</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Save this API key now!
            </Typography>
            <Typography variant="body2">
              This is the only time you will be able to see this key. Store it securely.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            API Key:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: '#f8fafc',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                flex: 1,
                wordBreak: 'break-all',
                fontWeight: 500,
              }}
            >
              {createdKey?.apiKey}
            </Typography>
            <Tooltip title={copiedToClipboard ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                size="small"
                onClick={handleCopyKey}
                sx={{
                  color: copiedToClipboard ? '#10b981' : '#06b6d4',
                  '&:hover': { backgroundColor: alpha('#06b6d4', 0.1) },
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#fef3c7', borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600} color="#92400e" gutterBottom>
              Configure your agent:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: '#78350f',
                whiteSpace: 'pre-wrap',
              }}
            >
              {`export API_KEY_EVENTS="${createdKey?.apiKey}"`}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={() => {
              setCreatedKeyDialog(false);
              setCreatedKey(null);
              setCopiedToClipboard(false);
            }}
            variant="contained"
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Are you sure you want to delete this API key? Agents using this key will no longer be able to
            authenticate.
          </Alert>
          <Typography
            variant="body2"
            sx={{
              backgroundColor: '#f8fafc',
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              fontWeight: 500,
            }}
          >
            {keyToDelete?.name}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={() => keyToDelete && deleteMutation.mutate(keyToDelete.id)}
            variant="contained"
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
